import React, { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Tooltip,
    Button,
} from '@mui/material';
import {
    Search,
    Close,
    CardGiftcard,
    Star,
    LocalOffer,
    Celebration,
    Campaign,
    EmojiEvents,
    Discount,
    Redeem,
    Loyalty,
    CardMembership,
    Bolt,
    FlashOn,
    Whatshot,
    TrendingUp,
    AttachMoney,
    MonetizationOn,
    Savings,
    AccountBalanceWallet,
    CreditCard,
    Payment,
    ShoppingCart,
    ShoppingBag,
    Store,
    Storefront,
    LocalMall,
    AddShoppingCart,
    RemoveShoppingCart,
    ShoppingBasket,
    Info,
    InfoOutlined,
    Announcement,
    NotificationImportant,
    NewReleases,
    Warning,
    Error,
    CheckCircle,
    CheckCircleOutline,
    Verified,
    VerifiedUser,
    Security,
    Lock,
    LockOpen,
    Key,
    VpnKey,
    Timer,
    Schedule,
    AccessTime,
    AlarmOn,
    AvTimer,
    HourglassEmpty,
    Timelapse,
    Update,
    Autorenew,
    Cached,
    Refresh,
    Sync,
    SyncAlt,
    Loop,
    AllInclusive,
    Favorite,
    FavoriteBorder,
    ThumbUp,
    ThumbDown,
    Recommend,
    Grade,
    Stars,
    AutoAwesome,
    ElectricBolt,
    ElectricCar,
    EvStation,
    DirectionsCar,
    LocalGasStation,
    BatteryChargingFull,
    Battery20,
    Battery60,
    Battery80,
    BatteryFull,
    PowerSettingsNew,
    Power,
    Speed,
    Timeline,
    TrendingDown,
    TrendingFlat,
    ShowChart,
    BarChart,
    PieChart,
    DonutSmall,
    DonutLarge,
    Analytics,
    Insights,
    QueryStats,
    DataUsage,
    Assessment,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';

// Define icon categories with their icons
const iconCategories = [
    {
        name: 'promotion',
        label: 'Promotions & Offers',
        icons: [
            { name: 'gift', icon: CardGiftcard, label: 'Gift' },
            { name: 'star', icon: Star, label: 'Star' },
            { name: 'local-offer', icon: LocalOffer, label: 'Offer' },
            { name: 'celebration', icon: Celebration, label: 'Celebration' },
            { name: 'campaign', icon: Campaign, label: 'Campaign' },
            { name: 'trophy', icon: EmojiEvents, label: 'Trophy' },
            { name: 'discount', icon: Discount, label: 'Discount' },
            { name: 'redeem', icon: Redeem, label: 'Redeem' },
            { name: 'loyalty', icon: Loyalty, label: 'Loyalty' },
            { name: 'membership', icon: CardMembership, label: 'Membership' },
        ],
    },
    {
        name: 'energy',
        label: 'Energy & Charging',
        icons: [
            { name: 'lightning-bolt', icon: Bolt, label: 'Lightning Bolt' },
            { name: 'flash', icon: FlashOn, label: 'Flash' },
            { name: 'fire', icon: Whatshot, label: 'Fire' },
            { name: 'electric-bolt', icon: ElectricBolt, label: 'Electric Bolt' },
            { name: 'electric-car', icon: ElectricCar, label: 'Electric Car' },
            { name: 'ev-station', icon: EvStation, label: 'EV Station' },
            { name: 'car', icon: DirectionsCar, label: 'Car' },
            { name: 'gas-station', icon: LocalGasStation, label: 'Gas Station' },
            { name: 'battery-charging', icon: BatteryChargingFull, label: 'Battery Charging' },
            { name: 'battery-full', icon: BatteryFull, label: 'Battery Full' },
            { name: 'power', icon: Power, label: 'Power' },
            { name: 'speed', icon: Speed, label: 'Speed' },
        ],
    },
    {
        name: 'finance',
        label: 'Finance & Money',
        icons: [
            { name: 'dollar', icon: AttachMoney, label: 'Dollar' },
            { name: 'money', icon: MonetizationOn, label: 'Money' },
            { name: 'savings', icon: Savings, label: 'Savings' },
            { name: 'wallet', icon: AccountBalanceWallet, label: 'Wallet' },
            { name: 'credit-card', icon: CreditCard, label: 'Credit Card' },
            { name: 'payment', icon: Payment, label: 'Payment' },
            { name: 'trending-up', icon: TrendingUp, label: 'Trending Up' },
            { name: 'trending-down', icon: TrendingDown, label: 'Trending Down' },
        ],
    },
    {
        name: 'shopping',
        label: 'Shopping & Store',
        icons: [
            { name: 'shopping-cart', icon: ShoppingCart, label: 'Shopping Cart' },
            { name: 'shopping-bag', icon: ShoppingBag, label: 'Shopping Bag' },
            { name: 'store', icon: Store, label: 'Store' },
            { name: 'storefront', icon: Storefront, label: 'Storefront' },
            { name: 'mall', icon: LocalMall, label: 'Mall' },
            { name: 'add-cart', icon: AddShoppingCart, label: 'Add to Cart' },
            { name: 'shopping-basket', icon: ShoppingBasket, label: 'Shopping Basket' },
        ],
    },
    {
        name: 'notification',
        label: 'Notifications & Alerts',
        icons: [
            { name: 'info', icon: Info, label: 'Info' },
            { name: 'announcement', icon: Announcement, label: 'Announcement' },
            { name: 'notification', icon: NotificationImportant, label: 'Notification' },
            { name: 'new', icon: NewReleases, label: 'New' },
            { name: 'warning', icon: Warning, label: 'Warning' },
            { name: 'check-circle', icon: CheckCircle, label: 'Check Circle' },
            { name: 'verified', icon: Verified, label: 'Verified' },
        ],
    },
    {
        name: 'time',
        label: 'Time & Schedule',
        icons: [
            { name: 'timer', icon: Timer, label: 'Timer' },
            { name: 'schedule', icon: Schedule, label: 'Schedule' },
            { name: 'clock', icon: AccessTime, label: 'Clock' },
            { name: 'alarm', icon: AlarmOn, label: 'Alarm' },
            { name: 'hourglass', icon: HourglassEmpty, label: 'Hourglass' },
            { name: 'timelapse', icon: Timelapse, label: 'Timelapse' },
            { name: 'update', icon: Update, label: 'Update' },
        ],
    },
    {
        name: 'favorite',
        label: 'Favorites & Ratings',
        icons: [
            { name: 'heart', icon: Favorite, label: 'Heart' },
            { name: 'heart-outline', icon: FavoriteBorder, label: 'Heart Outline' },
            { name: 'thumb-up', icon: ThumbUp, label: 'Thumb Up' },
            { name: 'recommend', icon: Recommend, label: 'Recommend' },
            { name: 'grade', icon: Grade, label: 'Grade' },
            { name: 'stars', icon: Stars, label: 'Stars' },
            { name: 'auto-awesome', icon: AutoAwesome, label: 'Auto Awesome' },
        ],
    },
    {
        name: 'analytics',
        label: 'Analytics & Charts',
        icons: [
            { name: 'timeline', icon: Timeline, label: 'Timeline' },
            { name: 'chart', icon: ShowChart, label: 'Chart' },
            { name: 'bar-chart', icon: BarChart, label: 'Bar Chart' },
            { name: 'pie-chart', icon: PieChart, label: 'Pie Chart' },
            { name: 'donut', icon: DonutLarge, label: 'Donut' },
            { name: 'analytics', icon: Analytics, label: 'Analytics' },
            { name: 'insights', icon: Insights, label: 'Insights' },
            { name: 'assessment', icon: Assessment, label: 'Assessment' },
        ],
    },
];

interface IconPickerProps {
    source: string;
    label?: string;
    value?: string;
    onChange?: (value: string) => void;
}

const IconPicker: React.FC<IconPickerProps> = ({ source, label, value, onChange }) => {
    const translate = useTranslate();
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Find the current icon component
    const getCurrentIcon = () => {
        for (const category of iconCategories) {
            const foundIcon = category.icons.find(i => i.name === value);
            if (foundIcon) {
                return foundIcon;
            }
        }
        return null;
    };

    const currentIcon = getCurrentIcon();

    // Filter icons based on search
    const filteredCategories = iconCategories.map(category => ({
        ...category,
        icons: category.icons.filter(icon =>
            icon.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            icon.name.toLowerCase().includes(searchTerm.toLowerCase())
        ),
    })).filter(category => category.icons.length > 0);

    const handleIconSelect = (iconName: string) => {
        if (onChange) {
            onChange(iconName);
        }
        setOpen(false);
        setSearchTerm('');
    };

    return (
        <>
            <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {label || translate('resources.banners.fields.iconName')}
                </Typography>
                <Button
                    variant="outlined"
                    onClick={() => setOpen(true)}
                    fullWidth
                    sx={{
                        justifyContent: 'flex-start',
                        textTransform: 'none',
                        py: 1.5,
                        px: 2,
                        backgroundColor: '#FAFAFA',
                        borderColor: '#E0E0E0',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            borderColor: '#D0D0D0',
                        },
                    }}
                >
                    {currentIcon ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <currentIcon.icon sx={{ color: '#00C853' }} />
                            <Typography variant="body1">{currentIcon.label}</Typography>
                        </Box>
                    ) : (
                        <Typography variant="body1" color="text.secondary">
                            {translate('resources.banners.helpers.selectIcon')}
                        </Typography>
                    )}
                </Button>
            </Box>

            <Dialog
                open={open}
                onClose={() => {
                    setOpen(false);
                    setSearchTerm('');
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ borderBottom: '1px solid #E0E0E0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {translate('resources.banners.iconPicker.title')}
                        </Typography>
                        <IconButton onClick={() => {
                            setOpen(false);
                            setSearchTerm('');
                        }}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <TextField
                        fullWidth
                        placeholder={translate('resources.banners.iconPicker.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                        {filteredCategories.map((category) => (
                            <Box key={category.name} sx={{ mb: 3 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {category.label}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {category.icons.map((icon) => (
                                        <Tooltip key={icon.name} title={icon.label}>
                                            <IconButton
                                                onClick={() => handleIconSelect(icon.name)}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: value === icon.name ? '#00C853' : '#E0E0E0',
                                                    backgroundColor: value === icon.name ? 'rgba(0, 200, 83, 0.08)' : 'transparent',
                                                    '&:hover': {
                                                        borderColor: '#00C853',
                                                        backgroundColor: 'rgba(0, 200, 83, 0.04)',
                                                    },
                                                }}
                                            >
                                                <icon.icon sx={{ color: value === icon.name ? '#00C853' : '#6B7280' }} />
                                            </IconButton>
                                        </Tooltip>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default IconPicker;